import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import type { User } from '../types';
import type { Session } from '@supabase/supabase-js';

interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * useAuth Hook
 * 
 * Manages authentication state for the application using Supabase Auth.
 * Handles login, logout, session persistence, and token refresh.
 * 
 * Features:
 * - Persistent login state using Supabase session storage
 * - Automatic token refresh
 * - Login/logout functionality
 * - Loading states during authentication
 * - Error handling for authentication failures
 * - User profile with healthcare system information
 */
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  // Fetch user profile with healthcare system info
  const fetchUserProfile = useCallback(async (userId: string) => {
    try {
      // Use RPC function to get full profile with healthcare system
      const { data, error } = await supabase.rpc('get_user_profile');
      
      if (error) {
        console.warn('Could not fetch user profile via RPC, trying direct query:', error);
        // Fallback to direct query
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          // Use auth user as fallback
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser) {
            setUser({
              id: authUser.id,
              email: authUser.email || '',
              full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name,
              role: 'owner',
              created_at: authUser.created_at,
              updated_at: authUser.updated_at || authUser.created_at,
            });
          }
          return;
        }

        if (profileData) {
          setUser(profileData as User);
        }
      } else if (data && data.length > 0) {
        setUser(data[0] as User);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  }, []);

  // Check for existing session on mount and listen for auth changes
  useEffect(() => {
    // Check initial session
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setLoading(false);
          return;
        }

        if (session) {
          setSession(session);
          // Fetch user profile with healthcare system info
          await fetchUserProfile(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: Session | null) => {
        console.log('Auth state changed:', event);
        
        if (event === 'SIGNED_IN' && session) {
          setSession(session);
          await fetchUserProfile(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          setSession(session);
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  // Login function
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Sign in with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('No user returned from authentication');
      }

      // Set session immediately to ensure isAuthenticated works
      if (authData.session) {
        setSession(authData.session);
      } else {
        // If session wasn't in response, get it
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSession(session);
        }
      }

      // Fetch user profile with healthcare system info
      await fetchUserProfile(authData.user.id);

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
      }
    setUser(null);
      setSession(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Request password reset (sends email)
  // Security: Always returns success to prevent email enumeration attacks
  // Supabase doesn't reveal whether an email exists or not - it returns success either way
  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      // Supabase returns success even if email doesn't exist (security best practice)
      // Only show error for actual technical failures (network issues, etc.)
      if (resetError) {
        // Log for debugging but don't expose to user to prevent email enumeration
        console.warn('Password reset error (logged but not exposed):', resetError);
        // Only throw for critical errors that prevent the request from being processed
        // Most errors from Supabase are handled gracefully and don't reveal email existence
        if (resetError.message?.toLowerCase().includes('network') || 
            resetError.message?.toLowerCase().includes('timeout') ||
            resetError.message?.toLowerCase().includes('connection')) {
          throw resetError;
        }
        // For other errors, return success to maintain security (prevent email enumeration)
        // The user will see a generic success message
        return true;
      }

      // Always return true - user sees generic success message regardless of email existence
      return true;
    } catch (err: any) {
      // Only show error for real technical failures (network, connection issues)
      const errorMessage = err.message || 'Connection error. Please check your internet connection and try again.';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update password with token from email link
  const updatePassword = useCallback(async (newPassword: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update password.';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    user, 
    loading, 
    error, 
    login, 
    logout, 
    clearError,
    resetPassword,
    updatePassword,
    // User is authenticated if they have a valid session
    // User profile can be fetched asynchronously after authentication
    isAuthenticated: !!session,
    session, // Expose session for API calls if needed
  };
};
